import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function MotorTransport() {
  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Regulatory Compliance',
      description: 'Strict adherence to U.S. and international road transportation regulations and safety standards.',
    },
    {
      icon: 'ri-truck-line',
      title: 'Modern Fleet',
      description: 'Well-maintained fleet of specialized petroleum transport vehicles.',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Flexible Routing',
      description: 'Direct door-to-door delivery with customizable routes and schedules.',
    },
    {
      icon: 'ri-time-line',
      title: 'Fast Delivery',
      description: 'Quick turnaround times for urgent and time-sensitive shipments.',
    },
    {
      icon: 'ri-gps-line',
      title: 'Real-Time Tracking',
      description: 'GPS tracking and monitoring for complete shipment visibility.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Professional Drivers',
      description: 'Experienced and certified drivers trained in petroleum product handling.',
    },
  ];

  const advantages = [
    'Door-to-door delivery service',
    'Flexible scheduling options',
    'Ideal for short to medium distances',
    'Quick response times',
    'Access to remote locations',
    'Cost-effective for smaller volumes',
    'Integration with other transport modes',
    'Comprehensive insurance coverage',
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section 
          className="relative min-h-[500px] flex items-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 20, 60, 0.7), rgba(0, 20, 60, 0.7)), url('https://public.readdy.ai/ai/img_res/99d66076553c614b2a2630fd5f23dd27.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">Motor Transport Services</h1>
            <p className="text-xl text-gray-200 max-w-3xl leading-relaxed">
              Professional road transportation services conducted in strict adherence to regulatory standards for safe and efficient petroleum delivery
            </p>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20petroleum%20tanker%20truck%20fleet%20at%20professional%20transport%20depot%2C%20authentic%20commercial%20photograph%20of%20genuine%20fuel%20transport%20vehicles%20with%20safety%20equipment%20and%20professional%20operations%2C%20documentary%20style%20photography%20of%20working%20motor%20transport%20showing%20real%20professional%20logistics%20and%20regulatory%20compliance%2C%20realistic%20photojournalism%20of%20actual%20tanker%20truck%20operations%20at%20facility&width=600&height=400&seq=motor-fleet-001&orientation=landscape"
                  alt="Motor Transport Fleet"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20professional%20petroleum%20tanker%20truck%20being%20loaded%20at%20terminal%20facility%2C%20authentic%20commercial%20photograph%20of%20genuine%20fuel%20transport%20vehicle%20loading%20operations%20with%20safety%20protocols%20and%20professional%20equipment%2C%20documentary%20style%20photography%20of%20working%20motor%20transport%20showing%20real%20professional%20logistics%20and%20regulatory%20compliance%20for%20petroleum%20delivery%2C%20realistic%20photojournalism%20of%20actual%20tanker%20truck%20loading&width=600&height=400&seq=motor-loading-001&orientation=landscape"
                  alt="Loading Operations"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20petroleum%20tanker%20truck%20fleet%20at%20professional%20transport%20facility%2C%20authentic%20documentary%20photography%20of%20actual%20fuel%20transport%20vehicles%20with%20safety%20equipment%20and%20company%20branding%2C%20professional%20photojournalism%20showing%20genuine%20motor%20transport%20operations%20with%20real%20trucks%20and%20logistics%20infrastructure%2C%20realistic%20commercial%20photograph%20of%20working%20tanker%20truck%20fleet&width=600&height=400&seq=motor-fleet-001&orientation=landscape"
                  alt="Motor Transport Fleet"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Professional Road Transportation</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Our road transportation services for petroleum products are conducted in strict adherence to U.S. and international regulatory standards. We prioritize safety and reliability above all, delivering a seamless and cost-effective logistics solution tailored to our clients' needs.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Motor transport offers unmatched flexibility for petroleum product delivery, providing direct door-to-door service that other transportation modes cannot match. Our modern fleet of specialized tanker trucks is equipped with the latest safety features and monitoring systems.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  With experienced drivers trained in petroleum product handling and emergency response, we ensure your shipments are transported safely and efficiently. Our real-time tracking systems provide complete visibility throughout the delivery process, giving you peace of mind from pickup to final destination.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Motor Transport Features</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive road transportation solutions with professional management and safety focus
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Motor Transport Advantages</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Road transportation provides unique advantages for petroleum product delivery, offering flexibility, speed, and direct access that complement our other transportation services. Our motor transport solutions are ideal for a wide range of shipping scenarios.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {advantages.map((advantage, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                      <span className="text-gray-700">{advantage}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Safety & Standards</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-government-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">DOT Compliance</h4>
                      <p className="text-gray-600">Full compliance with Department of Transportation regulations</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-star-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Safety Equipment</h4>
                      <p className="text-gray-600">Advanced safety features on all transport vehicles</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-star-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Driver Training</h4>
                      <p className="text-gray-600">Certified drivers with specialized petroleum handling training</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-tools-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Vehicle Maintenance</h4>
                      <p className="text-gray-600">Regular inspection and maintenance programs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Delivery Process</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Order & Schedule</h3>
                <p className="text-gray-600">Booking and route planning coordination</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Loading & Inspection</h3>
                <p className="text-gray-600">Safe loading with pre-trip inspection</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transport & Tracking</h3>
                <p className="text-gray-600">GPS-monitored delivery with updates</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Delivery & Confirmation</h3>
                <p className="text-gray-600">Safe unloading and delivery verification</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Request Motor Transport</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Contact us to discuss your petroleum road transportation requirements
            </p>
            <Link 
              to="/contact" 
              className="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}